import axios from "axios";
import authService from "@/services/authService";
import "firebase/compat/auth";

const isDev = process.env.NODE_ENV === "development";

// Base URL for consultasV2 (emulator or prod). Export for fetch() calls that must bypass axios.
export function getConsultasApiV1BaseURL() {
  if (process.env.NODE_ENV === "development") {
    if (process.env.VUE_APP_LOCAL_API_URL) {
      return process.env.VUE_APP_LOCAL_API_URL;
    }
    return "http://127.0.0.1:5001/saludsapro/us-central1/consultasV2/api/v1/";
  }
  return "https://us-central1-saludsapro.cloudfunctions.net/consultasV2/api/v1/";
}

const api = axios.create({
  baseURL: getConsultasApiV1BaseURL(),
  timeout: 30000,
});

if (isDev) {
  console.log("API Base URL:", getConsultasApiV1BaseURL());
}

function sanitizeLogData(data) {
  if (!data || typeof data !== "object") return data;
  const sensitiveKeys = ["dni", "ruc", "cedula", "numeroCedula", "numeroRuc"];
  const copy = { ...data };
  for (const key of sensitiveKeys) {
    if (key in copy) copy[key] = "[redacted]";
  }
  return copy;
}

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    if (isDev) {
      console.log(
        `[Axios] ${config.method?.toUpperCase()} ${config.url}`,
        sanitizeLogData(config.data),
      );
    }

    try {
      const authToken = await authService.getIdToken();
      const appCheckToken = authService.getAppCheckToken();

      if (appCheckToken) {
        config.headers["X-Firebase-AppCheck"] = appCheckToken;
      }

      if (authToken) {
        config.headers["Authorization"] = `Bearer ${authToken}`;
      }

      return config;
    } catch (error) {
      console.error("[Axios] Request interceptor error:", error);
      return config;
    }
  },
  (error) => Promise.reject(error),
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    if (isDev) {
      console.log(
        `[Axios] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
      );
    }
    return response;
  },
  async (error) => {
    if (isDev) {
      console.error(
        `[Axios] Error ${error.response?.status} ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
        error.response?.data?.message || error.message,
      );
    }

    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        await authService.initialize();

        const authToken = await authService.getIdToken();
        const appCheckToken = authService.getAppCheckToken();

        if (appCheckToken) {
          originalRequest.headers["X-Firebase-AppCheck"] = appCheckToken;
        }

        if (authToken) {
          originalRequest.headers["Authorization"] = `Bearer ${authToken}`;
        }

        return api(originalRequest);
      } catch (refreshError) {
        console.error("[Axios] Token refresh failed:", refreshError);
        await authService.signOut();

        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }

        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
